// Report 1 on the Reports page, client profitability, and the expense list behind it (dashboard_report_profitability,
// dashboard_save_expense_line, dashboard_save_expense_month). Super admins only: the costs include pay and commissions.

export type CostType='delivery'|'sales'|'overhead';
export type Allocation='leads'|'even'|'revenue'|'none';
export type Category='people'|'marketing'|'software'|'services'|'fees'|'financing'|'other';
export type AmountSource='month'|'usual'|'percent'|'missing'|'off';
export type Num=number|string;
export type ExpenseLine={line_id:string;name:string;category:Category;cost_type:CostType;allocation:Allocation;amount:Num|null;source:AmountSource;
 monthly_amount:Num|null;revenue_pct:Num|null;month_amount:Num|null;starts_on:string;ends_on:string|null;note:string|null;revision:number};
export type ProfitClient={business_id:string;client_name:string;manager:string|null;stage:string;billing_model:string|null;leads:number;
 revenue:Num;ads:Num;shared:Num;costs:Num;gross_profit:Num;margin_pct:Num|null;terms_missing:boolean};
export type Company={client_revenue:Num;elsewhere_revenue:Num;elsewhere_leads:number;revenue:Num;linked_ads:Num;unlinked_ads:Num;delivery_lines:Num;
 delivery:Num;gross_profit:Num;gross_margin_pct:Num|null;sales:Num;overhead:Num;net_profit:Num;net_margin_pct:Num|null;billed_clients:number;leads:number};
export type Profitability={month:string;generated_at:string;projected:boolean;share_of_month:Num;goal_margin_pct:number;last_synced_on:string|null;
 company:Company;clients:ProfitClient[];lines:ExpenseLine[];gaps:{missing_amounts:string[];terms_missing:string[];unlinked_ads:Num|null}};

export const costTypeLabels:Record<CostType,string>={delivery:'Delivery',sales:'Sales',overhead:'Overhead'};
export const costTypeHints:Record<CostType,string>={delivery:'Getting clients their leads: counted in gross margin',sales:'Winning clients: trials, setters, closers, commissions',
 overhead:'Running the business'};
export const allocationLabels:Record<Allocation,string>={leads:'By leads',even:'Evenly between paying clients',revenue:'By what they pay',none:'Not shared'};
export const categoryLabels:Record<Category,string>={people:'People',marketing:'Marketing',software:'Software',services:'Services',fees:'Fees',financing:'Financing',other:'Other'};
export const sourceLabels:Record<AmountSource,string>={month:'Set for this month',usual:'Usual amount',percent:'Share of billing',missing:'No amount yet',off:'Not running this month'};

export const n=(v:Num|null|undefined)=>v===null||v===undefined||v===''?null:Number(v);
/** Green at or above the goal, amber within 10 points of it, red below that. */
export function marginTone(pct:Num|null,goal:number):'green'|'amber'|'red'|'none'{
 const p=n(pct);
 if(p===null)return 'none';
 return p>=goal?'green':p>=goal-10?'amber':'red';
}

/** Dollars typed or pasted: "$1,333", "440", "89.50". Blank is null. */
export function readDollars(text:string):{ok:true;value:string|null}|{ok:false}{
 const t=text.trim().replace(/^\$/,'').replace(/,/g,'');
 if(!t)return {ok:true,value:null};
 return /^\d{1,9}(\.\d{1,2})?$/.test(t)?{ok:true,value:t}:{ok:false};
}
const same=(a:Num|null,b:string|null)=>(a===null?null:Number(a))===(b===null?null:Number(b));

/** This month's amounts typed in the grid: only the ones that changed, or the first that is not an amount. */
export function monthAmountsRequest(month:string,lines:ExpenseLine[],drafts:Record<string,string>):{ok:true;request:{month:string;amounts:{line_id:string;amount:string|null}[]}}|{ok:false;error:string}{
 const amounts:{line_id:string;amount:string|null}[]=[];
 for(const l of lines){
  if(!(l.line_id in drafts))continue;
  const r=readDollars(drafts[l.line_id]);
  if(!r.ok)return {ok:false,error:`${l.name}: enter an amount in dollars, like 440 or 89.50, or leave it blank for the usual amount.`};
  if(!same(l.month_amount,r.value))amounts.push({line_id:l.line_id,amount:r.value});
 }
 return {ok:true,request:{month,amounts}};
}

/** A block pasted from the sheet: on each row a name, then its amount ("CallRail	$440"). Cells before the name (a
 * category) and after the amount (a note) are skipped, and so are totals and rows with no amount (section headings). */
export function readPaste(text:string,lines:ExpenseLine[]){
 const byName=new Map(lines.map(l=>[l.name.trim().toLowerCase(),l]));
 const matched:{line:ExpenseLine;amount:string}[]=[],unknown:{name:string;amount:string}[]=[];
 for(const raw of text.split(/\r?\n/)){
  const cells=(raw.includes('\t')?raw.split('\t'):raw.split(/,(?=\s*\$?\d)/)).map(c=>c.trim());
  const at=cells.findIndex(c=>/\d/.test(c)&&readDollars(c).ok);
  const name=at>0?[...cells.slice(0,at)].reverse().find(c=>c!==''):undefined;
  if(!name||/^(sub)?total/i.test(name))continue;
  const amount=readDollars(cells[at]) as {ok:true;value:string};
  const line=byName.get(name.toLowerCase());
  if(line)matched.push({line,amount:amount.value});else unknown.push({name,amount:amount.value});
 }
 return {matched,unknown};
}

export type LineDraft={name:string;category:Category;cost_type:CostType;allocation:Allocation;basis:'amount'|'percent';amount:string;percent:string;
 starts_on:string;ends_on:string;note:string};
export const newLineDraft=(month:string):LineDraft=>({name:'',category:'software',cost_type:'delivery',allocation:'leads',basis:'amount',amount:'',percent:'',
 starts_on:month,ends_on:'',note:''});
export const toLineDraft=(l:ExpenseLine):LineDraft=>({name:l.name,category:l.category,cost_type:l.cost_type,allocation:l.allocation==='none'?'leads':l.allocation,
 basis:l.revenue_pct!==null?'percent':'amount',amount:l.monthly_amount===null?'':String(Number(l.monthly_amount)),
 percent:l.revenue_pct===null?'':String(Number(l.revenue_pct)),starts_on:l.starts_on.slice(0,10),ends_on:l.ends_on?.slice(0,10)??'',note:l.note??''});

/** The request for a new cost, or the changes to one. */
export function lineRequest(d:LineDraft,line?:ExpenseLine):{ok:true;request:Record<string,unknown>}|{ok:false;error:string}{
 const name=d.name.trim();
 if(!name)return {ok:false,error:'Name the cost.'};
 if(name.length>80)return {ok:false,error:'Keep the name to 80 characters.'};
 const amount=readDollars(d.amount);
 if(d.basis==='amount'&&!amount.ok)return {ok:false,error:'Enter the usual amount in dollars, like 440 or 89.50, or leave it blank if not known yet.'};
 const pct=d.percent.trim().replace(/%$/,'');
 if(d.basis==='percent'&&!(/^\d{1,2}(\.\d{1,2})?$/.test(pct)&&Number(pct)>0&&Number(pct)<=50))return {ok:false,error:'Enter the percent of billing, between 0.01 and 50.'};
 if(d.ends_on&&d.ends_on<d.starts_on)return {ok:false,error:'The last month is before the first.'};
 if(d.note.trim().length>500)return {ok:false,error:'Keep the note to 500 characters.'};
 const request:Record<string,unknown>={name,category:d.category,cost_type:d.cost_type,allocation:d.cost_type==='delivery'?d.allocation:'none',
  starts_on:d.starts_on,ends_on:d.ends_on||null,note:d.note.trim()||null,
  ...(d.basis==='percent'?{revenue_pct:pct,monthly_amount:null}:{monthly_amount:amount.ok?amount.value:null,revenue_pct:null})};
 return {ok:true,request:line?{...request,line_id:line.line_id,expected_revision:line.revision}:request};
}

/** Months to pick the first and last month from: a year back and a year ahead. */
export function monthChoices(from:Date=new Date()){
 const out:{value:string;label:string}[]=[];
 for(let i=-12;i<=12;i++){
  const d=new Date(Date.UTC(from.getUTCFullYear(),from.getUTCMonth()+i,1));
  out.push({value:d.toISOString().slice(0,10),label:d.toLocaleDateString('en-US',{month:'short',year:'numeric',timeZone:'UTC'})});
 }
 return out;
}
